import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { FileText, Download, Loader2 } from 'lucide-react';

const ExcelViewer = ({ filePath, fileName = 'Excel File' }) => {
  const [sheetData, setSheetData] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useParsedView, setUseParsedView] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const iframeRef = useRef(null);
  const checkTimeoutRef = useRef(null);

  useEffect(() => {
    const loadExcelFile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch the Excel file
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error('Failed to load Excel file');
        }
        
        const arrayBuffer = await response.arrayBuffer();
        // Read with cellStyles to preserve formatting
        const workbook = XLSX.read(arrayBuffer, { 
          type: 'array',
          cellStyles: true,
          cellNF: true // Preserve number formats
        });
        
        // Get sheet names
        const sheets = workbook.SheetNames;
        setSheetNames(sheets);
        setActiveSheet(sheets[0] || '');
        
        // Parse the first sheet with styles
        if (sheets.length > 0) {
          parseSheet(workbook.Sheets[sheets[0]], sheets[0]);
        }
        
        setLoading(false);
        
        // Check if Office viewer will work (requires public HTTPS URL)
        // If localhost or not HTTPS, use parsed view
        if (typeof window !== 'undefined') {
          const isLocalhost = window.location.hostname === 'localhost' || 
                            window.location.hostname === '127.0.0.1' ||
                            window.location.hostname === '';
          const isNotHttps = window.location.protocol !== 'https:';
          
          if (isLocalhost || isNotHttps) {
            // Office viewer won't work, use parsed view
            setUseParsedView(true);
          } else {
            // Try Office viewer, but set timeout to fallback
            setTimeout(() => {
              // If viewer hasn't loaded after 5 seconds, switch to parsed view
              if (!useParsedView) {
                setUseParsedView(true);
              }
            }, 5000);
          }
        }
      } catch (err) {
        console.error('Error loading Excel file:', err);
        setError(err.message);
        setLoading(false);
        setUseParsedView(true);
      }
    };

    loadExcelFile();
    
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [filePath]);

  // Helper: interpolate between two rgb colors
  const lerp = (a, b, t) => a + (b - a) * t;
  const lerpColor = (c1, c2, t) => {
    const r = Math.round(lerp(c1[0], c2[0], t));
    const g = Math.round(lerp(c1[1], c2[1], t));
    const b = Math.round(lerp(c1[2], c2[2], t));
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Helper function to calculate heatmap color (red → orange → yellow → green)
  const getHeatmapColor = (value, min, max) => {
    if (min === max || value === null || value === undefined || isNaN(value)) {
      return null;
    }

    // Normalize value between 0 and 1
    const t = (value - min) / (max - min);

    // Tailwind-ish palette:
    // red-500:    (239, 68, 68)  #ef4444
    // orange-500: (249, 115, 22) #f97316
    // yellow-500: (234, 179, 8)  #eab308
    // green-500:  (34, 197, 94)  #22c55e
    const RED = [239, 68, 68];
    const ORANGE = [249, 115, 22];
    const YELLOW = [234, 179, 8];
    const GREEN = [34, 197, 94];

    if (t <= 1 / 3) {
      return lerpColor(RED, ORANGE, t * 3);
    }
    if (t <= 2 / 3) {
      return lerpColor(ORANGE, YELLOW, (t - 1 / 3) * 3);
    }
    return lerpColor(YELLOW, GREEN, (t - 2 / 3) * 3);
  };

  const parseSheet = (sheet, sheetName) => {
    if (!sheet || !sheet['!ref']) return;
    
    // Get the range of the sheet
    const range = XLSX.utils.decode_range(sheet['!ref']);
    
    // Store cell data with styles
    const cells = [];
    const widths = {};
    
    // First pass: find Price_USD and UltimateParent columns
    let priceUsdColumnIndex = -1;
    let ultimateParentColumnIndex = -1;
    const priceValues = [];
    
    // Get headers from first row
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c: C });
      const cell = sheet[cellAddress];
      if (cell) {
        const headerValue = cell.v || cell.w || '';
        const headerLower = String(headerValue).toLowerCase();
        if (headerLower.includes('price_usd') || headerLower.includes('price')) {
          priceUsdColumnIndex = C;
        }
        if (
          headerLower.includes('ultimateparent') ||
          headerLower.includes('ultimate_parent') ||
          headerLower.includes('ultimate parent') ||
          headerLower.includes('assignee')
        ) {
          ultimateParentColumnIndex = C;
        }
      }
    }
    
    // Collect Price_USD values for heatmap
    if (priceUsdColumnIndex >= 0) {
      for (let R = range.s.r + 1; R <= range.e.r; R++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: priceUsdColumnIndex });
        const cell = sheet[cellAddress];
        if (cell && cell.v !== undefined && cell.v !== null) {
          const numValue = typeof cell.v === 'number' ? cell.v : parseFloat(cell.v);
          if (!isNaN(numValue)) {
            priceValues.push(numValue);
          }
        }
      }
    }
    
    // Calculate min and max for heatmap
    const minPrice = priceValues.length > 0 ? Math.min(...priceValues) : 0;
    const maxPrice = priceValues.length > 0 ? Math.max(...priceValues) : 0;
    
    // Get column widths if available
    if (sheet['!cols']) {
      sheet['!cols'].forEach((col, index) => {
        if (col && col.wpx) {
          widths[index] = col.wpx;
        }
      });
    }
    
    // Process each cell
    for (let R = range.s.r; R <= range.e.r; R++) {
      const row = [];
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = sheet[cellAddress];
        
        if (cell) {
          // Get cell value
          let value = '';
          if (cell.v !== undefined && cell.v !== null) {
            value = cell.v;
          } else if (cell.w !== undefined) {
            value = cell.w;
          } else if (cell.t === 's') {
            value = cell.v || '';
          }
          
          // Get cell style
          const style = getCellStyle(cell);
          
          // Style header row (first row) with black background and white bold text
          if (R === range.s.r) {
            style.backgroundColor = '#000000';
            style.color = '#ffffff';
            style.fontWeight = 'bold';
          }
          
          // Apply heatmap coloring to Price_USD column (skip header row)
          if (C === priceUsdColumnIndex && R > range.s.r && priceValues.length > 0) {
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            if (!isNaN(numValue)) {
              const heatmapColor = getHeatmapColor(numValue, minPrice, maxPrice);
              if (heatmapColor) {
                style.backgroundColor = heatmapColor;
                // All text in Price_USD column is black
                style.color = '#000000';
              }
            }
          }
          
          // Apply conditional coloring to UltimateParent column (skip header row)
          if (C === ultimateParentColumnIndex && R > range.s.r) {
            const cellValue = String(value).trim();
            if (cellValue.toLowerCase().includes('company a')) {
              style.backgroundColor = '#dbeafe'; // Very light blue (blue-100)
              style.color = '#000000';
            } else if (cellValue.toLowerCase().includes('company b')) {
              style.backgroundColor = '#dcfce7'; // Very light green (green-100)
              style.color = '#000000';
            }
          }
          
          // Check for merged cells
          let colSpan = 1;
          let rowSpan = 1;
          if (sheet['!merges']) {
            sheet['!merges'].forEach(merge => {
              if (merge.s.r === R && merge.s.c === C) {
                colSpan = merge.e.c - merge.s.c + 1;
                rowSpan = merge.e.r - merge.s.r + 1;
              }
            });
          }
          
          row.push({
            value: value,
            style: style,
            colSpan: colSpan > 1 ? colSpan : undefined,
            rowSpan: rowSpan > 1 ? rowSpan : undefined,
            isEmpty: !value && !cell.s
          });
        } else {
          row.push({ value: '', style: {}, isEmpty: true });
        }
      }
      cells.push(row);
    }
    
    setSheetData(cells);
    setColumnWidths(widths);
  };

  const getCellStyle = (cell) => {
    if (!cell || !cell.s) return {};
    
    const style = cell.s;
    const cssStyle = {};
    
    // Background color
    if (style.fill && style.fill.fgColor) {
      const bgColor = excelColorToCSS(style.fill.fgColor);
      if (bgColor) {
        cssStyle.backgroundColor = bgColor;
      }
    }
    
    // Text color
    if (style.font && style.font.color) {
      const textColor = excelColorToCSS(style.font.color);
      if (textColor) {
        cssStyle.color = textColor;
      }
    }
    
    // Font bold
    if (style.font && style.font.bold) {
      cssStyle.fontWeight = 'bold';
    }
    
    // Font italic
    if (style.font && style.font.italic) {
      cssStyle.fontStyle = 'italic';
    }
    
    // Font size
    if (style.font && style.font.sz) {
      cssStyle.fontSize = `${style.font.sz}pt`;
    }
    
    // Font family
    if (style.font && style.font.name) {
      cssStyle.fontFamily = style.font.name;
    }
    
    // Text alignment
    if (style.alignment) {
      if (style.alignment.horizontal) {
        cssStyle.textAlign = style.alignment.horizontal;
      }
      if (style.alignment.vertical) {
        cssStyle.verticalAlign = style.alignment.vertical;
      }
      if (style.alignment.wrapText) {
        cssStyle.whiteSpace = 'normal';
        cssStyle.wordWrap = 'break-word';
      }
    }
    
    // Borders
    const borders = {};
    if (style.border) {
      ['top', 'bottom', 'left', 'right'].forEach(side => {
        if (style.border[side] && style.border[side].style) {
          const borderColor = excelColorToCSS(style.border[side].color) || '#000000';
          const borderWidth = style.border[side].style === 'thin' ? '1px' : 
                             style.border[side].style === 'medium' ? '2px' : 
                             style.border[side].style === 'thick' ? '3px' : '1px';
          borders[`border${side.charAt(0).toUpperCase() + side.slice(1)}`] = `${borderWidth} solid ${borderColor}`;
        }
      });
    }
    Object.assign(cssStyle, borders);
    
    return cssStyle;
  };

  const excelColorToCSS = (color) => {
    if (!color) return null;
    
    // If it's already a hex color string
    if (typeof color === 'string') {
      if (color.startsWith('#')) return color;
      if (color.startsWith('RGB')) {
        const matches = color.match(/\d+/g);
        if (matches && matches.length >= 3) {
          return `rgb(${matches[0]}, ${matches[1]}, ${matches[2]})`;
        }
      }
    }
    
    // If it's an object with rgb property (hex string)
    if (color.rgb) {
      const rgb = color.rgb;
      if (typeof rgb === 'string') {
        return rgb.startsWith('#') ? rgb : `#${rgb}`;
      }
    }
    
    // Handle theme colors (simplified - Excel has complex theme color system)
    if (color.theme !== undefined) {
      // Basic theme color mapping (this is simplified)
      const themeColors = {
        0: '#000000', // Text 1
        1: '#FFFFFF', // Background 1
        2: '#000000', // Text 2
        3: '#FFFFFF', // Background 2
        4: '#FF0000', // Accent 1
        5: '#00FF00', // Accent 2
        6: '#0000FF', // Accent 3
        7: '#FFFF00', // Accent 4
        8: '#FF00FF', // Accent 5
        9: '#00FFFF', // Accent 6
      };
      return themeColors[color.theme] || null;
    }
    
    return null;
  };

  const handleSheetChange = (sheetName) => {
    setActiveSheet(sheetName);
    // Reload data for the selected sheet
    fetch(filePath)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => {
        const workbook = XLSX.read(arrayBuffer, { 
          type: 'array',
          cellStyles: true,
          cellNF: true
        });
        parseSheet(workbook.Sheets[sheetName], sheetName);
      })
      .catch(err => {
        console.error('Error loading sheet:', err);
        setError('Failed to load sheet data');
      });
  };

  // Get the full URL for the file
  const fileUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}${filePath}`
    : filePath;

  // Microsoft Office Online viewer URL
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[800px] bg-gray-50 rounded-lg border border-gray-200">
        <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading Excel file...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 mb-4">
          Unable to load Excel file: {error}
        </p>
        <a
          href={filePath}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          download
        >
          <Download className="mr-2 h-5 w-5" />
          Download {fileName}
        </a>
      </div>
    );
  }

  // Use parsed view if Office viewer won't work or user requested it
  if (useParsedView || !sheetData) {
    if (!sheetData || sheetData.length === 0) {
      return (
        <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">No data available in this Excel file.</p>
        </div>
      );
    }

    // Find max columns to ensure consistent table structure
    const maxCols = Math.max(...sheetData.map(row => row.length));

    return (
      <div className="w-full">
        {/* Sheet selector */}
        {sheetNames.length > 1 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {sheetNames.map((sheetName) => (
              <button
                key={sheetName}
                onClick={() => handleSheetChange(sheetName)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSheet === sheetName
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {sheetName}
              </button>
            ))}
          </div>
        )}

        {/* Download button */}
        <div className="mb-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Viewing: <span className="font-medium">{fileName}</span>
            {!useParsedView && (
              <span className="ml-2 text-xs text-gray-500">(Parsed view - some formatting may differ)</span>
            )}
          </div>
          <a
            href={filePath}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
            download
          >
            <Download className="mr-2 h-4 w-4" />
            Download Excel
          </a>
        </div>

        {/* Excel table with preserved styling */}
        <div className="overflow-auto border border-gray-200 rounded-lg bg-white max-h-[800px]">
          <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
            <tbody>
              {sheetData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Array.from({ length: maxCols }).map((_, colIndex) => {
                    const cell = row[colIndex] || { value: '', style: {}, isEmpty: true };
                    
                    return (
                      <td
                        key={colIndex}
                        colSpan={cell.colSpan}
                        rowSpan={cell.rowSpan}
                        className="px-2 py-1 text-sm border border-gray-300"
                        style={{
                          minWidth: columnWidths[colIndex] ? `${columnWidths[colIndex]}px` : '80px',
                          whiteSpace: cell.style.whiteSpace || 'nowrap',
                          ...cell.style
                        }}
                      >
                        {cell.value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Try Office Online viewer
  return (
    <div className="w-full">
      {/* Sheet selector */}
      {sheetNames.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {sheetNames.map((sheetName) => (
            <button
              key={sheetName}
              onClick={() => setActiveSheet(sheetName)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSheet === sheetName
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {sheetName}
            </button>
          ))}
        </div>
      )}

      {/* Download button */}
      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Viewing: <span className="font-medium">{fileName}</span>
        </div>
        <a
          href={filePath}
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          download
        >
          <Download className="mr-2 h-4 w-4" />
          Download Excel
        </a>
      </div>

      {/* Info banner */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> If the viewer doesn't load,{' '}
          <button
            onClick={() => setUseParsedView(true)}
            className="text-blue-600 hover:text-blue-800 underline font-medium"
          >
            click here to use parsed view
          </button>
          {' '}or{' '}
          <a
            href={filePath}
            className="text-blue-600 hover:text-blue-800 underline font-medium"
            download
          >
            download the file
          </a>
          {' '}to open in Excel.
        </p>
      </div>

      {/* Office Online viewer */}
      <div className="w-full">
        <iframe
          ref={iframeRef}
          src={officeViewerUrl}
          className="w-full h-[800px] border border-gray-200 rounded-lg"
          title={fileName}
          onLoad={() => {
            // Set timeout to check if viewer actually loaded
            checkTimeoutRef.current = setTimeout(() => {
              // If still showing loading after 3 seconds, switch to parsed view
              setUseParsedView(true);
            }, 3000);
          }}
        >
          <div className="p-8 text-center bg-gray-50 rounded-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              Unable to display Excel file in browser. Please download to view:
            </p>
            <a
              href={filePath}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              download
            >
              <Download className="mr-2 h-5 w-5" />
              Download {fileName}
            </a>
          </div>
        </iframe>
      </div>
    </div>
  );
};

export default ExcelViewer;
